import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PaperAirplaneIcon,
  UserCircleIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PageHeader, Button, Badge, FormField, TextInput, TextArea } from '../components/ui';
import {
  communicationsService,
  ConversationSummary,
  ConversationDetail,
  ConversationMessage,
  ContactDirectory,
} from '../services/communicationsService';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

function formatRelative(dateStr?: string | null) {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 1) return 'Just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}h ago`;
  if (diffMin < 10080) return `${Math.floor(diffMin / 1440)}d ago`;
  return d.toLocaleDateString();
}

function formatTime(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function initialsOf(name?: string | null) {
  if (!name) return '?';
  return name
    .split(' ')
    .filter(Boolean)
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

type Filter = 'all' | 'internal' | 'clients';

export default function Communication() {
  const { user } = useAuth();
  const { showToast } = useToast();

  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [activeDetail, setActiveDetail] = useState<ConversationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<Filter>('all');
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [newModalOpen, setNewModalOpen] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const refreshList = useCallback(async () => {
    try {
      setLoading(true);
      const list = await communicationsService.listConversations();
      setConversations(list);
    } catch (err: any) {
      showToast(err?.message || 'Failed to load conversations', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  const loadDetail = useCallback(
    async (id: string) => {
      try {
        setDetailLoading(true);
        const detail = await communicationsService.getConversation(id);
        setActiveDetail(detail);
        setConversations((prev) =>
          prev.map((c) => (c.id === id ? { ...c, unreadCount: 0 } : c)),
        );
      } catch (err: any) {
        showToast(err?.message || 'Failed to load conversation', 'error');
      } finally {
        setDetailLoading(false);
      }
    },
    [showToast],
  );

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (activeId) loadDetail(activeId);
  }, [activeId, loadDetail]);

  useEffect(() => {
    if (activeDetail) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [activeDetail?.messages?.length]);

  useEffect(() => {
    const interval = setInterval(() => {
      refreshList();
      if (activeId) loadDetail(activeId);
    }, 20000);
    return () => clearInterval(interval);
  }, [refreshList, loadDetail, activeId]);

  const filteredConversations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return conversations.filter((c) => {
      if (activeFilter === 'internal' && c.type !== 'INTERNAL') return false;
      if (activeFilter === 'clients' && c.type === 'INTERNAL') return false;
      if (!q) return true;
      const hay = [
        c.subject,
        c.client?.name,
        c.client?.companyName,
        c.lastMessage?.body,
        ...c.participants.map((p) => p.user?.name || p.emailAddress || ''),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [conversations, searchQuery, activeFilter]);

  const handleSend = async () => {
    if (!newMessage.trim() || !activeId || sending) return;
    const body = newMessage.trim();
    setSending(true);
    try {
      const msg = await communicationsService.sendMessage(activeId, body, 'IN_APP');
      setNewMessage('');
      setActiveDetail((prev) => (prev ? { ...prev, messages: [...prev.messages, msg] } : prev));
      refreshList();
    } catch (err: any) {
      showToast(err?.message || 'Failed to send message', 'error');
    } finally {
      setSending(false);
    }
  };

  const titleOf = (c: ConversationSummary | ConversationDetail): string => {
    if (c.subject) return c.subject;
    if (c.client?.name) {
      return c.client.companyName ? `${c.client.name} — ${c.client.companyName}` : c.client.name;
    }
    const others = c.participants
      .map((p) => p.user?.name || p.emailAddress || '')
      .filter((n) => n && n !== user?.name);
    return others.join(', ') || 'Conversation';
  };

  return (
    <div className="comm-page">
      <PageHeader
        title="Communication"
        subtitle="Messages, email threads, and client conversations in one place."
        actions={
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setNewModalOpen(true)}>
            New message
          </Button>
        }
      />

      <div className="comm-layout">
        <aside className="comm-sidebar">
          <div className="comm-sidebar-search">
            <MagnifyingGlassIcon className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search conversations…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="comm-filters">
            {(['all', 'internal', 'clients'] as Filter[]).map((f) => (
              <button
                key={f}
                className={`comm-filter-chip ${activeFilter === f ? 'active' : ''}`}
                onClick={() => setActiveFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'internal' ? 'Team' : 'Clients'}
              </button>
            ))}
          </div>

          <div className="comm-thread-list">
            {loading && conversations.length === 0 ? (
              <div className="comm-empty">Loading conversations…</div>
            ) : filteredConversations.length === 0 ? (
              <div className="comm-empty">
                <ChatBubbleLeftRightIcon className="w-8 h-8" />
                <p>No conversations yet</p>
                <Button variant="secondary" size="sm" onClick={() => setNewModalOpen(true)}>
                  Start a conversation
                </Button>
              </div>
            ) : (
              filteredConversations.map((c) => {
                const isActive = c.id === activeId;
                return (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className={`comm-thread-item ${isActive ? 'active' : ''}`}
                  >
                    <div className="comm-avatar">
                      {c.type === 'CLIENT' ? (
                        <BuildingOfficeIcon className="w-5 h-5" />
                      ) : (
                        <span>{initialsOf(titleOf(c))}</span>
                      )}
                    </div>
                    <div className="comm-thread-body">
                      <div className="comm-thread-top">
                        <span className="comm-thread-name">{titleOf(c)}</span>
                        <span className="comm-thread-time">{formatRelative(c.lastMessageAt || c.createdAt)}</span>
                      </div>
                      <div className="comm-thread-snippet">
                        <span>{c.lastMessage?.body || 'No messages yet'}</span>
                        {c.unreadCount > 0 && (
                          <Badge tone="info">{c.unreadCount}</Badge>
                        )}
                      </div>
                      {c.client?.companyName && (
                        <div className="comm-thread-meta">
                          <BuildingOfficeIcon className="w-3 h-3" />
                          <span>{c.client.companyName}</span>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </aside>

        <section className="comm-main">
          {!activeDetail ? (
            <div className="comm-empty-main">
              <ChatBubbleLeftRightIcon className="w-12 h-12" />
              <p>Select a conversation to view messages</p>
            </div>
          ) : (
            <>
              <header className="comm-main-header">
                <div className="comm-avatar comm-avatar-lg">
                  {activeDetail.type === 'CLIENT' ? (
                    <BuildingOfficeIcon className="w-5 h-5" />
                  ) : (
                    <span>{initialsOf(titleOf(activeDetail))}</span>
                  )}
                </div>
                <div className="comm-main-title">
                  <h3>{titleOf(activeDetail)}</h3>
                  <p>
                    {activeDetail.participants
                      .map((p) => p.user?.name || p.emailAddress)
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                </div>
                <Badge tone={activeDetail.type === 'CLIENT' ? 'warning' : 'neutral'}>
                  {activeDetail.type === 'CLIENT' ? 'Client' : activeDetail.type === 'EMAIL' ? 'Email' : 'Team'}
                </Badge>
              </header>

              <div className="comm-messages">
                {detailLoading && activeDetail.messages.length === 0 ? (
                  <div className="comm-empty">Loading…</div>
                ) : activeDetail.messages.length === 0 ? (
                  <div className="comm-empty">No messages yet. Send the first message below.</div>
                ) : (
                  activeDetail.messages.map((m: ConversationMessage, idx) => {
                    const isMine = m.senderUserId === user?.id;
                    const prev = activeDetail.messages[idx - 1];
                    const showDate =
                      !prev || new Date(prev.createdAt).toDateString() !== new Date(m.createdAt).toDateString();
                    return (
                      <React.Fragment key={m.id}>
                        {showDate && (
                          <div className="comm-date-divider">
                            <span>{new Date(m.createdAt).toLocaleDateString()}</span>
                          </div>
                        )}
                        <div className={`comm-message ${isMine ? 'mine' : ''}`}>
                          {!isMine && (
                            <div className="comm-avatar comm-avatar-sm">
                              <span>{initialsOf(m.senderName || m.senderEmail)}</span>
                            </div>
                          )}
                          <div className="comm-message-bubble">
                            {!isMine && (
                              <div className="comm-message-sender">
                                {m.senderName || m.senderEmail}
                              </div>
                            )}
                            <div className="comm-message-body">{m.body}</div>
                            <div className="comm-message-time">
                              {m.channel === 'EMAIL' && <EnvelopeIcon className="w-3 h-3" />}
                              {formatTime(m.createdAt)}
                            </div>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              <footer className="comm-composer">
                <TextArea
                  rows={2}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type a message…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <Button
                  variant="primary"
                  leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
                  disabled={!newMessage.trim() || sending}
                  loading={sending}
                  onClick={handleSend}
                >
                  Send
                </Button>
              </footer>
            </>
          )}
        </section>
      </div>

      {newModalOpen && (
        <NewConversationModal
          onClose={() => setNewModalOpen(false)}
          onCreated={(conv) => {
            setNewModalOpen(false);
            refreshList();
            setActiveId(conv.id);
          }}
        />
      )}
    </div>
  );
}

function NewConversationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (conv: ConversationDetail) => void;
}) {
  const { showToast } = useToast();
  const [contacts, setContacts] = useState<ContactDirectory>({ team: [], clients: [] });
  const [mode, setMode] = useState<'INTERNAL' | 'CLIENT'>('INTERNAL');
  const [subject, setSubject] = useState('');
  const [clientId, setClientId] = useState('');
  const [userIds, setUserIds] = useState<string[]>([]);
  const [initialMessage, setInitialMessage] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    communicationsService.getContacts().then(setContacts).catch(() => null);
  }, []);

  const canCreate =
    !!initialMessage.trim() &&
    (mode === 'INTERNAL' ? userIds.length > 0 : !!clientId);

  const handleCreate = async () => {
    if (!canCreate) return;
    setCreating(true);
    try {
      const conv = await communicationsService.createConversation({
        type: mode,
        subject: subject.trim() || undefined,
        clientId: mode === 'CLIENT' ? clientId : null,
        participantUserIds: mode === 'INTERNAL' ? userIds : undefined,
        initialMessage: initialMessage.trim(),
      });
      onCreated(conv as any);
    } catch (err: any) {
      showToast(err?.message || 'Failed to start conversation', 'error');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container modal-sm" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">New conversation</h2>
          <button type="button" className="modal-close-btn" onClick={onClose} aria-label="Close">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="modal-body">
          <div className="ui-wizard-section">
            <div className="ui-wizard-radio-group">
              {(
                [
                  { id: 'INTERNAL' as const, label: 'Team chat', desc: 'Start a conversation with one or more teammates.' },
                  { id: 'CLIENT' as const, label: 'Client thread', desc: 'Message a client. They will see it in their portal.' },
                ]
              ).map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`ui-wizard-radio ${mode === opt.id ? 'selected' : ''}`}
                  onClick={() => setMode(opt.id)}
                >
                  <span className="ui-wizard-radio-indicator" />
                  <span>
                    <span className="ui-wizard-radio-label">{opt.label}</span>
                    <span className="ui-wizard-radio-desc">{opt.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <FormField label="Subject (optional)" htmlFor="conv-subject">
            <TextInput
              id="conv-subject"
              placeholder="e.g. Boiler follow-up"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </FormField>

          {mode === 'INTERNAL' ? (
            <FormField label="Team members" help="Select one or more teammates">
              <div className="comm-contact-picker">
                {contacts.team.length === 0 ? (
                  <div className="comm-empty comm-empty-sm">No teammates available</div>
                ) : (
                  contacts.team.map((m) => {
                    const selected = userIds.includes(m.id);
                    return (
                      <button
                        key={m.id}
                        type="button"
                        className={`comm-contact-chip ${selected ? 'selected' : ''}`}
                        onClick={() =>
                          setUserIds((prev) =>
                            prev.includes(m.id) ? prev.filter((x) => x !== m.id) : [...prev, m.id],
                          )
                        }
                      >
                        <UserCircleIcon className="w-4 h-4" />
                        <span>{m.name}</span>
                        <span className="comm-contact-role">{m.role}</span>
                      </button>
                    );
                  })
                )}
              </div>
            </FormField>
          ) : (
            <FormField label="Client" required>
              <select
                className="ui-input"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
              >
                <option value="">Select a client…</option>
                {contacts.clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.companyName ? ` — ${c.companyName}` : ''}
                  </option>
                ))}
              </select>
            </FormField>
          )}

          <FormField label="First message" required>
            <TextArea
              rows={3}
              value={initialMessage}
              onChange={(e) => setInitialMessage(e.target.value)}
              placeholder="Type your message…"
            />
          </FormField>
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button variant="primary" disabled={!canCreate} loading={creating} onClick={handleCreate}>
            Start conversation
          </Button>
        </div>
      </div>
    </div>
  );
}
